import {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useMutation, useQuery} from '@apollo/client'
import {Map, List} from 'immutable'

import AppEnrollment from './AppEnrollment'
import BackupCode from './BackupCode'
import ConfirmCodeForm from './ConfirmCodeForm'
import Enabled from './Enabled'
import TfaMethodSelectionStep from './TfaMethodSelectionStep'
import PrimaryPhoneForm from './PrimaryPhoneForm'
import SecondaryPhoneForm from './SecondaryPhoneForm'

import {cleanGraphQLResponse} from '../../../../../lib/graphQLTools'
import {
  CREATE_TFA_PHONE_NUMBER,
  CONFIRM_PRIMARY_TFA_NUMBER,
  CREATE_TFA_BACKUP_CODES,
  CREATE_TFA_ENROLLMENT,
  VERIFY_TFA_ENROLLMENT,
  DELETE_TFA_ENROLLMENT,
  TOGGLE_TFA_STATUS
} from '../../../../../graphql/mutations/tfa'
import {TFA_ENROLLMENTS, BACKUP_CODES} from '../../../../../graphql/queries/tfa'
import Logger from '../../../../../lib/NewLogger'
import MixpanelLogger from '../../../../../lib/MixpanelLogger'

import './tfaWizardModal.scss'


const TfaWizardStep = props => {
  switch (props.step) {
    case 'START':
      return (
        <TfaMethodSelectionStep
          loading={props.creatingAppEnrollment}
          onCancel={props.onCancelSetup}
          onNext={props.startEnrollment}
          onSelectMethod={props.selectEnrollmentMode}
          selectedMethod={props.enrollmentMode}
        />
      )
    case 'APP':
      return <AppEnrollment {...props} />
    case 'PHONE':
      return (
        <PrimaryPhoneForm
          {...props}
          onBack={props.restartEnrollment}
          createPrimaryPhoneNumber={props.createPrimaryPhoneNumber}
        />
      )
    case 'CONFIRM':
      return (
        <ConfirmCodeForm
          {...props}
          confirmCode={props.confirmCode}
          onBack={props.enrollmentMode === 'phone' ? props.startPhoneEnrollment : props.restartEnrollment}
          phoneNumber={props.pendingNumber}
          phoneVerified={props.phoneVerified}
          restartFunction={props.startPhoneEnrollment}
        />
      )
    case 'SECONDARY':
      return <SecondaryPhoneForm {...props} />
    case 'BACKUP':
      return (
        <BackupCode
          {...props}
          onBack={props.restartEnrollment}
        />
      )
    case 'ENABLED':
      return <Enabled {...props} />
    default:
      return null
  }
}

TfaWizardStep.propTypes = {
  beginTfaPhoneEnrollment: PropTypes.func,
  confirmCode: PropTypes.func,
  createPrimaryPhoneNumber: PropTypes.func,
  creatingAppEnrollment: PropTypes.bool,
  enrollmentMode: PropTypes.string,
  onCancelSetup: PropTypes.func,
  pendingNumber: PropTypes.object,
  phoneVerified: PropTypes.bool,
  restartEnrollment: PropTypes.func,
  selectEnrollmentMode: PropTypes.func,
  startEnrollment: PropTypes.func,
  startPhoneEnrollment: PropTypes.func,
  step: PropTypes.string.isRequired
}

const TfaWizard = props => {
  const [step, setStep] = useState(props.appEnrollmentOnly ? 'APP' : 'START')
  const [enrollmentMode, setEnrollmentMode] = useState(props.appEnrollmentOnly ? 'app' : 'phone')
  const [phoneVerified, setPhoneVerified] = useState(false)
  const [confirmationError, setConfirmationError] = useState('')
  const [creatingAppEnrollment, setCreatingAppEnrollment] = useState(false)
  const [backupCodes, setBackupCodes] = useState([])
  const [tfaEnrollment, setTfaEnrollment] = useState(Map())

  const {data: tfaBackupCodes} = useQuery(BACKUP_CODES)
  const [createTfaPhoneNumber, {data: createdPhoneNumber}] = useMutation(CREATE_TFA_PHONE_NUMBER, {
    awaitRefetchQueries: true,
    refetchQueries: [{query: TFA_ENROLLMENTS}]
  })
  const [confirmPrimaryTfaNumber] = useMutation(CONFIRM_PRIMARY_TFA_NUMBER)
  const [createTfaBackupCodes] = useMutation(CREATE_TFA_BACKUP_CODES)
  const [createTfaEnrollment] = useMutation(CREATE_TFA_ENROLLMENT, {awaitRefetchQueries: true, refetchQueries: [{query: TFA_ENROLLMENTS}]})
  const [deleteTfaEnrollment] = useMutation(DELETE_TFA_ENROLLMENT, {awaitRefetchQueries: true, refetchQueries: [{query: TFA_ENROLLMENTS}]})
  const [verifyTfaEnrollment] = useMutation(VERIFY_TFA_ENROLLMENT, {awaitRefetchQueries: true, refetchQueries: [{query: TFA_ENROLLMENTS}]})
  const [toggleTfaStatus] = useMutation(TOGGLE_TFA_STATUS, {awaitRefetchQueries: true, refetchQueries: [{query: TFA_ENROLLMENTS}, {query: BACKUP_CODES}]})

  const existingBackupCodes = cleanGraphQLResponse(tfaBackupCodes, 'backupCodes', List())

  const nextStep = enrollmentStep => {
    setConfirmationError('') // prevent persisting error messages across steps
    setStep(enrollmentStep)
  }

  const beginAppEnrollement = () => {
    const input = {name: 'Authenticator app', mode: 'app', category: 'primary'}

    setCreatingAppEnrollment(true)
    createTfaEnrollment({variables: {input}, onCompleted: response => {
      setTfaEnrollment(cleanGraphQLResponse(response, 'createTfaEnrollment', Map()))
      nextStep('APP')
      setCreatingAppEnrollment(false)
    }, onError: () => { setCreatingAppEnrollment(false) }})
  }

  const logMixpanelEvent = name => {
    MixpanelLogger.track(name)
  }

  // auto create app tfa enrollment for users that are only allowed to enroll in app method
  useEffect(() => {
    if (props.appEnrollmentOnly) {
      beginAppEnrollement()
      logMixpanelEvent(`2fa_setup_${enrollmentMode}`)
    }
  }, [])

  // remove dangling enrollments on mount
  useEffect(() => {
    const danglingEnrollments = (props.tfaEnrollments || List()).filter(enrollment => !enrollment.get('enabled-at'))

    danglingEnrollments.forEach(enrollment => {
      deleteTfaEnrollment({variables: {id: enrollment.get('id')}})
    })
  }, [])

  const selectEnrollmentMode = selectedMethod => {
    setEnrollmentMode(selectedMethod)
  }

  const beginTfaPhoneEnrollment = () => {
    nextStep('PHONE')
  }

  const startEnrollment = () => {
    setConfirmationError('')
    logMixpanelEvent(`2fa_setup_${enrollmentMode}`)

    if (enrollmentMode === 'phone')
      beginTfaPhoneEnrollment()
    else
      beginAppEnrollement()
  }

  const restartEnrollment = () => {
    if (enrollmentMode === 'phone') {
      const danglingEnrollments = (props.tfaEnrollments || List()).filter(enrollment => !enrollment.get('enabled-at'))

      danglingEnrollments.forEach(enrollment => {
        deleteTfaEnrollment({variables: {id: enrollment.get('id')}})
      })
    }

    nextStep(enrollmentMode === 'phone' ? 'START' : 'APP')
  }

  const generateBackupCodes = () => {
    if (existingBackupCodes.isEmpty()) {
      createTfaBackupCodes({
        variables: {tfaConfigId: props.tfaConfig.id},
        onCompleted: response => {
          setPhoneVerified(true)

          setTimeout(() => {
            setBackupCodes(response?.createTfaBackupCodes?.backupCodes || [])
            setPhoneVerified(false)
            nextStep('BACKUP')
          }, 500)
        }
      })
    } else if (!tfaEnrollment.isEmpty()) {
      const payload = {tfaConfigId: props.tfaConfig.id, enabled: true, enrollmentId: tfaEnrollment.get('id')}

      toggleTfaStatus({
        variables: {input: payload},
        onCompleted: () => { nextStep('ENABLED') }
      })
    }
  }

  const createPrimaryPhoneNumber = phoneNumberInput => {
    createTfaPhoneNumber({
      variables: {input: {...phoneNumberInput, tfaConfigId: props.tfaConfig.id, primary: true}},
      onCompleted: () => { nextStep('CONFIRM') }
    })
  }

  const confirmPrimaryNumber = formData => {
    setConfirmationError('')
    const {createTfaPhoneNumber: tfaPhoneNumber} = createdPhoneNumber || {}

    confirmPrimaryTfaNumber({
      variables: {input: formData, phoneId: tfaPhoneNumber?.id},
      onCompleted: generateBackupCodes,
      onError: () => setConfirmationError('Incorrect code. Please try again.')
    })
  }

  const verifyEnrollment = formData => {
    setConfirmationError('')

    verifyTfaEnrollment({
      variables: {id: tfaEnrollment.get('id'), input: formData},
      onCompleted: generateBackupCodes,
      onError: () => setConfirmationError('Incorrect code. Please try again.')
    })
  }

  const logEvent = name => {
    Logger.log({name, payload: {}})
  }

  return (
    <TfaWizardStep
      {...props}
      backupCodes={backupCodes}
      creatingAppEnrollment={creatingAppEnrollment}
      error={confirmationError}
      tfaEnrollments={props.tfaEnrollments}
      tfaEnrollment={tfaEnrollment}
      step={step}
      enrollmentMode={enrollmentMode}
      nextStep={nextStep}
      startEnrollment={startEnrollment}
      startPhoneEnrollment={beginTfaPhoneEnrollment}
      selectEnrollmentMode={selectEnrollmentMode}
      toggleTfaStatus={toggleTfaStatus}
      restartEnrollment={restartEnrollment}
      createPrimaryPhoneNumber={createPrimaryPhoneNumber}
      confirmCode={confirmPrimaryNumber}
      phoneVerified={phoneVerified}
      pendingNumber={cleanGraphQLResponse(createdPhoneNumber, 'createTfaPhoneNumber', Map())}
      verifyEnrollment={verifyEnrollment}
      logEvent={logEvent}
      logMixpanelEvent={logMixpanelEvent}
    />
  )
}

TfaWizard.propTypes = {
  appEnrollmentOnly: PropTypes.bool,
  exitTfa: PropTypes.func,
  onCancelSetup: PropTypes.func,
  tfaConfig: PropTypes.object,
  tfaEnrollments: PropTypes.instanceOf(List)
}

export default TfaWizard
